import React from "react";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFileAlt } from "react-icons/fa";

const CorporateSleekQuotation = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        expiryDate,
        items,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-slate-900 min-h-[1100px] flex overflow-hidden font-sans">
            {/* Dark Sidebar */}
            <div className="w-[30%] p-12 text-slate-400 flex flex-col justify-between">
                <div className="space-y-12">
                    <div className="space-y-6">
                        {companyDetails?.logo ? (
                            <img src={companyDetails.logo} alt="Logo" className="h-16 w-auto object-contain brightness-0 invert" />
                        ) : (
                            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-white text-2xl font-black">
                                {companyDetails?.name?.charAt(0)}
                            </div>
                        )}
                        <div className="space-y-1">
                            <h2 className="text-white font-black text-xl tracking-tighter">{companyDetails?.name}</h2>
                            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 whitespace-nowrap overflow-hidden text-ellipsis">{companyDetails?.address}</p>
                        </div>
                    </div>

                    <div className="space-y-8">
                        <section className="space-y-3 font-bold text-xs">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Contact</h4>
                            <div className="space-y-2 text-slate-300">
                                <div className="flex items-center gap-3"><FaPhone /> {companyDetails?.phone}</div>
                                <div className="flex items-center gap-3"><FaEnvelope /> {companyDetails?.email}</div>
                            </div>
                        </section>

                        <section className="space-y-3 font-bold text-xs">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Legal Entity</h4>
                            <div className="space-y-1 text-slate-300">
                                <div className="text-white">REG: 772-202X</div>
                                <div>{companyDetails?.bankName}</div>
                            </div>
                        </section>
                    </div>
                </div>

                <div className="space-y-2 pt-10 border-t border-white/5">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4 items-center flex gap-2">
                        <FaFileAlt className="text-emerald-500" /> Formal Estimate
                    </div>
                    <p className="text-[10px] leading-relaxed text-slate-600">
                        {terms || "This quotation is subject to terms and conditions. Prices are estimates and may vary based on actual requirements."}
                    </p>
                </div>
            </div>

            {/* Light Main Content */}
            <div className="flex-1 bg-white rounded-l-[3rem] p-16 flex flex-col justify-between">
                <div>
                    <div className="flex justify-between items-end mb-20">
                        <div className="space-y-1">
                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Proposed To</span>
                            <h2 className="text-4xl font-black text-slate-900 tracking-tighter">{to}</h2>
                            <p className="text-sm font-medium text-slate-500 max-w-xs">{clientAddress}</p>
                        </div>
                        <div className="text-right space-y-1">
                            <h1 className="text-7xl font-black text-slate-100 uppercase leading-none mb-4 -mr-10">QUOTE</h1>
                            <div className="text-sm font-black text-slate-900 uppercase tracking-widest"># {title}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">Valid Until: {expiryDate}</div>
                        </div>
                    </div>

                    <div className="mb-12">
                        <table className="w-full text-left">
                            <thead className="border-b-2 border-slate-900">
                                <tr className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                    <th className="pb-6">Service / Component</th>
                                    <th className="pb-6 text-center">Qty</th>
                                    <th className="pb-6 text-right">Unit Price</th>
                                    <th className="pb-6 text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-bold text-xs uppercase tracking-tighter">
                                {(items || []).map((item, idx) => (
                                    <tr key={idx} className="group italic whitespace-nowrap overflow-hidden text-ellipsis">
                                        <td className="py-8 pr-10">
                                            <div className="text-lg font-black text-slate-900 group-hover:text-emerald-600 transition-colors uppercase italic tracking-tighter">{item.item}</div>
                                        </td>
                                        <td className="py-8 text-center text-slate-400">{item.qty}</td>
                                        <td className="py-8 text-right text-slate-900">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                        <td className="py-8 text-right text-lg font-black text-slate-900 uppercase">Nu. {parseFloat(item.amount).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="space-y-10">
                    <div className="flex justify-between items-start pt-10 border-t-4 border-slate-900">
                        <div className="max-w-sm">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 italic whitespace-nowrap overflow-hidden text-ellipsis underline underline-offset-4 decoration-slate-900 decoration-2">Project Notes</h4>
                            <p className="text-sm font-medium text-slate-500 leading-relaxed italic">
                                "{notes || "We are excited about the possibility of working on this project. Our team is ready to deliver excellence."}"
                            </p>
                        </div>
                        <div className="w-72 space-y-6">
                            <div className="space-y-3 font-bold text-xs uppercase tracking-widest text-slate-400 italic">
                                <div className="flex justify-between">
                                    <span>Subtotal</span>
                                    <span className="text-slate-900 font-black">Nu. {parseFloat(subTotal).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Estimated Tax ({taxRate}%)</span>
                                    <span className="text-slate-900 font-black">Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-rose-500">
                                        <span>Consolidation Discount</span>
                                        <span className="font-black">- Nu. {parseFloat(discount).toLocaleString()}</span>
                                    </div>
                                )}
                            </div>
                            <div className="pt-8 border-t-2 border-slate-100 flex justify-between items-end">
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-900 italic">Grand Estimate</span>
                                <div className="text-4xl font-black text-emerald-600 tracking-tighter italic">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-[0.3em] text-slate-300">
                        <div>Corporate Strategic Document</div>
                        <div>Generated via NexrBook Pro</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CorporateSleekQuotation;
